import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material'
import { Plus, Trash2, Radio, LayoutGrid, Users, Mic, Link2, BarChart3 } from 'lucide-react'
import {
  useGetListeningTopicsQuery,
  useDeleteListeningTopicMutation,
  Platform,
} from 'graphql/generated/graphql'
import CreateTopicDialog from './components/CreateTopicDialog'
import TopicFeed from './components/TopicFeed'
import AllTopicsFeeds from './components/AllTopicsFeeds'
import InfoTooltip from 'components/InfoTooltip/InfoTooltip'
import EngagementStatsBar from './components/EngagementStatsBar'
import EpisodePerformance from './components/EpisodePerformance'
import SuggestedProfiles from './components/SuggestedProfiles'
import AudienceInsights from './components/AudienceInsights'
import PodcastOnboarding from './components/PodcastOnboarding'
import PromoteEpisodeDialog from './components/PromoteEpisodeDialog'
import ClipToSignalDialog from './components/ClipToSignalDialog'

const platformIcons: Record<Platform, string> = {
  [Platform.Instagram]: '📷',
  [Platform.TikTok]: '🎵',
  [Platform.Youtube]: '🎥',
  [Platform.Reddit]: '🔴',
  [Platform.Facebook]: '📘',
  [Platform.Twitter]: '🐦',
  [Platform.Bluesky]: '🦋',
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return 'Never'
  const date = new Date(dateString)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  return date.toLocaleDateString()
}

export default function ListeningTopics() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false)
  const [clipDialogOpen, setClipDialogOpen] = useState(false)
  const [showAllFeeds, setShowAllFeeds] = useState(false)
  const [showProfiles, setShowProfiles] = useState(false)
  const [showInsights, setShowInsights] = useState(false)
  const [selectedTopic, setSelectedTopic] = useState<{
    id: string
    name: string
    platforms: Platform[]
    lastScrapedAt: string | null
  } | null>(null)

  const { data, loading, refetch } = useGetListeningTopicsQuery()
  const [deleteTopic] = useDeleteListeningTopicMutation()

  const topics = data?.getListeningTopics ?? []

  const handleDelete = async (e: React.MouseEvent, topicId: string) => {
    e.stopPropagation()
    if (!window.confirm('Delete this topic and all its content?')) return
    await deleteTopic({ variables: { id: topicId } })
    void refetch()
  }

  // Multi-topic feeds view
  if (showAllFeeds) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1800, mx: 'auto' }}>
        <AllTopicsFeeds onBack={() => setShowAllFeeds(false)} />
      </Box>
    )
  }

  // Audience Insights view
  if (showInsights) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1000, mx: 'auto' }}>
        <AudienceInsights onBack={() => setShowInsights(false)} />
      </Box>
    )
  }

  // Suggested profiles view
  if (showProfiles) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1000, mx: 'auto' }}>
        <SuggestedProfiles onBack={() => setShowProfiles(false)} />
      </Box>
    )
  }

  // If a topic is selected, show its feed
  if (selectedTopic) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1800, mx: 'auto' }}>
        <TopicFeed
          topicId={selectedTopic.id}
          topicName={selectedTopic.name}
          topicPlatforms={selectedTopic.platforms}
          lastScrapedAt={selectedTopic.lastScrapedAt}
          onBack={() => setSelectedTopic(null)}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 2, mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', display: 'flex', alignItems: 'center', gap: 1.5, fontSize: { xs: '24px', md: '34px' } }}>
            <Radio size={24} />
            Social Radar<InfoTooltip text="Your Social Radar scans Instagram, TikTok, YouTube, Reddit, and Twitter daily for conversations related to your podcast topics. Each card is a signal — click to see the conversations it found." />
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5, fontSize: { xs: '13px', md: '16px' } }}>
            Find and engage with conversations that grow your podcast audience.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
          {topics.length >= 1 && (
            <Button
              onClick={() => setPromoteDialogOpen(true)}
              startIcon={<Mic size={16} />}
              sx={{
                color: 'white',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '12px',
                textTransform: 'none',
                px: { xs: 2, md: 3 },
                py: 1,
                fontWeight: 600,
                fontSize: { xs: '13px', md: '14px' },
                '&:hover': { borderColor: '#a855f7', backgroundColor: 'rgba(168, 85, 247, 0.1)' },
              }}
            >
              Promote Episode
            </Button>
          )}
          {topics.length >= 1 && (
            <Button
              onClick={() => setClipDialogOpen(true)}
              startIcon={<Link2 size={16} />}
              sx={{
                color: 'white',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '12px',
                textTransform: 'none',
                px: { xs: 2, md: 3 },
                py: 1,
                fontWeight: 600,
                fontSize: { xs: '13px', md: '14px' },
                '&:hover': { borderColor: '#a855f7', backgroundColor: 'rgba(168, 85, 247, 0.1)' },
              }}
            >
              From Clip
            </Button>
          )}
          {topics.length >= 1 && (
            <Button
              onClick={() => setShowInsights(true)}
              startIcon={<BarChart3 size={16} />}
              sx={{
                color: 'white',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '12px',
                textTransform: 'none',
                px: { xs: 2, md: 3 },
                py: 1,
                fontWeight: 600,
                fontSize: { xs: '13px', md: '14px' },
                '&:hover': { borderColor: '#a855f7', backgroundColor: 'rgba(168, 85, 247, 0.1)' },
              }}
            >
              Insights
            </Button>
          )}
          {topics.length >= 1 && (
            <Button
              onClick={() => setShowProfiles(true)}
              startIcon={<Users size={16} />}
              sx={{
                color: 'white',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '12px',
                textTransform: 'none',
                px: { xs: 2, md: 3 },
                py: 1,
                fontWeight: 600,
                fontSize: { xs: '13px', md: '14px' },
                '&:hover': { borderColor: '#a855f7', backgroundColor: 'rgba(168, 85, 247, 0.1)' },
              }}
            >
              Network
            </Button>
          )}
          {topics.length >= 2 && (
            <Button
              onClick={() => setShowAllFeeds(true)}
              startIcon={<LayoutGrid size={16} />}
              sx={{
                color: 'white',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '12px',
                textTransform: 'none',
                px: { xs: 2, md: 3 },
                py: 1,
                fontWeight: 600,
                fontSize: { xs: '13px', md: '14px' },
                '&:hover': { borderColor: '#a855f7', backgroundColor: 'rgba(168, 85, 247, 0.1)' },
              }}
            >
              View Feeds
            </Button>
          )}
          <Button
            onClick={() => setCreateDialogOpen(true)}
            startIcon={<Plus size={16} />}
            sx={{
              background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
              color: 'white',
              borderRadius: '12px',
              textTransform: 'none',
              px: { xs: 2, md: 3 },
              py: 1,
              fontWeight: 600,
              fontSize: { xs: '13px', md: '14px' },
              '&:hover': { background: 'linear-gradient(135deg, #9333ea 0%, #4f46e5 100%)' },
            }}
          >
            Add Topic
          </Button>
        </Box>
      </Box>

      {/* Loading */}
      <EngagementStatsBar />
      <EpisodePerformance />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#a855f7' }} />
        </Box>
      )}

      {/* Podcast Onboarding (shown when no topics) */}
      {!loading && topics.length === 0 && (
        <PodcastOnboarding
          onComplete={() => { refetch().catch(console.error) }}
          onSkip={() => setCreateDialogOpen(true)}
        />
      )}

      {/* Topic Cards Grid */}
      {!loading && topics.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2 }}>
          {topics.map((topic) => (
            <Box
              key={topic.id}
              onClick={() =>
                setSelectedTopic({
                  id: topic.id,
                  name: topic.name,
                  platforms: topic.platforms,
                  lastScrapedAt: topic.lastScrapedAt ?? null,
                })
              }
              sx={{
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px',
                p: 3,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  borderColor: 'rgba(168, 85, 247, 0.3)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                },
              }}
            >
              {/* Top row: name + delete */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '16px', flex: 1, pr: 1 }}>
                  {topic.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => { handleDelete(e, topic.id).catch(() => {}) }}
                  sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#ef4444' } }}
                >
                  <Trash2 size={14} />
                </IconButton>
              </Box>

              {/* Description */}
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '13px',
                  lineHeight: 1.4,
                  mb: 2,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {topic.description}
              </Typography>

              {/* Platforms */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {topic.platforms.map((p) => (
                  <Chip
                    key={p}
                    label={`${platformIcons[p]} ${p}`}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '11px',
                      height: 24,
                    }}
                  />
                ))}
              </Box>

              {/* Follower range */}
              {(topic.minFollowers != null || topic.maxFollowers != null) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                  <Users size={12} color="rgba(168,85,247,0.6)" />
                  <Typography sx={{ color: 'rgba(168,85,247,0.6)', fontSize: '12px' }}>
                    {topic.minFollowers != null && topic.maxFollowers != null
                      ? `${topic.minFollowers.toLocaleString()} - ${topic.maxFollowers.toLocaleString()}`
                      : topic.minFollowers != null
                        ? `${topic.minFollowers.toLocaleString()}+`
                        : `Up to ${(topic.maxFollowers ?? 0).toLocaleString()}`} followers
                  </Typography>
                </Box>
              )}

              {/* Stats */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ color: 'rgba(168, 85, 247, 0.8)', fontSize: '13px', fontWeight: 500 }}>
                  {topic.contentCount} posts
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>
                  Updated: {formatDate(topic.lastScrapedAt)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <CreateTopicDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={() => { refetch().catch(() => {}) }}
      />

      <PromoteEpisodeDialog
        open={promoteDialogOpen}
        onClose={() => setPromoteDialogOpen(false)}
        onCreated={() => { refetch().catch(() => {}) }}
      />

      <ClipToSignalDialog
        open={clipDialogOpen}
        onClose={() => setClipDialogOpen(false)}
        onCreated={() => { refetch().catch(() => {}) }}
      />
    </Box>
  )
}
